import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ComposedChart,
	Legend,
	Line,
	LineChart,
	Pie,
	PieChart,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { cn } from "@/lib/utils";
import { chartColors } from "../../../theme/chartColors";
import {
	COMPARISON_OPTIONS,
	LakeQueryResponse,
	WidgetDisplay,
	WidgetSpec,
} from "../../../models/ReportBuilder";
import { fillGaps, formatBucketLabel, grainOfLabel } from "./dateRange";
import {
	THRESHOLD_CLASSES,
	arrangeRows,
	drillPath,
	formatNumber,
	formatPercent,
	isNumericColumn,
	thresholdColor,
	visibleColumns,
} from "./format";

export const PALETTE = [
	chartColors.brand,
	chartColors.action,
	chartColors.warning,
	chartColors.danger,
	"#8E7CC3",
	"#4DB6AC",
	"#F06292",
	"#A1887F",
];

const CMP = " (jmf)";
const cmpKey = (key: string) => `${key}${CMP}`;
const comparisonLabel = (data: LakeQueryResponse) =>
	COMPARISON_OPTIONS.find((o) => o.value === data.comparison?.mode)?.short ?? "jämförelse";

/** Percentage change from previous to current; null when there is nothing to compare against. */
export const deltaPercent = (current: unknown, previous: unknown): number | null => {
	const c = Number(current);
	const p = Number(previous);
	if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) return null;
	return ((c - p) / Math.abs(p)) * 100;
};

const Delta: React.FC<{
	current: unknown;
	previous: unknown;
	label: string;
	format: (v: unknown) => string;
}> = ({ current, previous, label, format }) => {
	const pct = deltaPercent(current, previous);
	if (pct === null)
		return (
			<span className="text-xs text-gray-400">
				ingen jämförelse ({label}: {format(previous)})
			</span>
		);
	const up = pct > 0;
	const flat = Math.abs(pct) < 0.05;
	return (
		<span
			className={
				flat
					? "text-xs text-gray-500"
					: up
					? "text-xs font-medium text-brand"
					: "text-xs font-medium text-danger"
			}
		>
			{flat ? "±0 %" : formatPercent(pct)} mot {label} ({format(previous)})
		</span>
	);
};

/** Current rows with the comparison rows merged in by position, under "<key> (jmf)" and a "__cmpLabel". */
function mergeComparison(
	data: LakeQueryResponse,
	xKey: string,
	yKeys: string[]
): Record<string, unknown>[] {
	const cmp = data.comparison?.rows ?? [];
	return data.rows.map((row, i) => {
		const other = cmp[i];
		if (!other) return row;
		const merged: Record<string, unknown> = { ...row, __cmpLabel: other[xKey] };
		yKeys.forEach((k) => (merged[cmpKey(k)] = other[k]));
		return merged;
	});
}

/** Picks x/y keys when the spec does not say: first text column as x, numeric columns as series. */
export function resolveKeys(spec: Omit<WidgetSpec, "sql">, data: LakeQueryResponse) {
	const columns = data.columns;
	const numeric = columns.filter((c) => isNumericColumn(data.rows, c));
	const xKey =
		spec.chart.xKey && columns.includes(spec.chart.xKey)
			? spec.chart.xKey
			: columns.find((c) => !numeric.includes(c)) ?? columns[0];
	const wanted = (spec.chart.yKeys ?? []).filter((k) => columns.includes(k));
	const yKeys = wanted.length ? wanted : numeric.filter((c) => c !== xKey);
	return { xKey, yKeys };
}

export interface ChartPoint {
	xKey: string;
	value: unknown;
	row: Record<string, unknown>;
}

interface WidgetChartProps {
	spec: WidgetSpec | Omit<WidgetSpec, "sql">;
	data: LakeQueryResponse;
	display?: WidgetDisplay;
	/** Pixel height, or "100%" to fill a sized parent (grid cards). */
	height?: number | string;
	/** Called when a bar, point, slice or table row is clicked. */
	onPointClick?: (point: ChartPoint) => void;
	/** Table rows shown before the list is cut (the maximize view shows all). */
	maxTableRows?: number;
	/**
	 * Number widgets only: "tile" centres the number in the card; "row" stacks
	 * number, delta and freshness right-aligned, for a card that puts its title
	 * beside the number (condensed TV mode).
	 */
	layout?: "tile" | "row";
}

/** Draws a widget's rows according to its chart definition and display options. */
const WidgetChart: React.FC<WidgetChartProps> = ({
	spec,
	data: raw,
	display,
	height = 260,
	onPointClick,
	maxTableRows = 200,
	layout = "tile",
}) => {
	const keys = useMemo(() => resolveKeys(spec, raw), [spec, raw]);
	const { xKey, yKeys } = keys;
	const data = useMemo(() => arrangeRows(raw, display, xKey), [raw, display, xKey]);
	const unit = display?.unit?.trim() || spec.chart.unit;
	const format = (v: unknown, u: string | undefined = unit) => formatNumber(v, u, display?.format);
	const axisFormat = (v: unknown) =>
		formatNumber(v, undefined, display?.format?.compact ? { compact: true } : undefined);
	const thresholds = display?.thresholds;

	if (!data.rows.length)
		return (
			<div className="flex h-full min-h-[120px] items-center justify-center text-sm text-gray-500">
				Inga rader för perioden
			</div>
		);

	const comparison = data.comparison;
	const cmpLabel = comparisonLabel(data);
	const clickable = !!onPointClick;

	if (spec.chart.type === "number") {
		const key = yKeys[0] ?? data.columns[0];
		const value = data.rows[0]?.[key];
		const color = thresholdColor(value, thresholds);
		if (layout === "row")
			return (
				<div className="flex h-full flex-col items-end justify-center gap-0.5 text-right">
					<div
						className={cn(
							"text-3xl font-semibold leading-tight tabular-nums",
							color ? THRESHOLD_CLASSES[color].text : "text-gray-900"
						)}
					>
						{format(value)}
					</div>
					{comparison && (
						<Delta
							current={value}
							previous={comparison.rows[0]?.[key]}
							label={cmpLabel}
							format={(v) => format(v)}
						/>
					)}
					{raw.dataAsOf && (
						<span className="text-[10px] text-gray-400">
							Data från{" "}
							{new Date(raw.dataAsOf).toLocaleString("sv-SE", {
								dateStyle: "short",
								timeStyle: "short",
							})}
						</span>
					)}
				</div>
			);
		return (
			<div
				className={cn(
					"flex h-full min-h-[120px] flex-col items-center justify-center gap-1 rounded-md",
					// Fitted to a screen (TV mode) the tile is only as tall as its numbers.
					"[[data-fit]_&]:min-h-0",
					color && THRESHOLD_CLASSES[color].bg
				)}
			>
				<div
					className={cn(
						"text-4xl font-semibold",
						color ? THRESHOLD_CLASSES[color].text : "text-gray-900"
					)}
				>
					{format(value)}
				</div>
				<div className="text-xs uppercase tracking-wide text-gray-500">{key}</div>
				{comparison && (
					<Delta
						current={value}
						previous={comparison.rows[0]?.[key]}
						label={cmpLabel}
						format={(v) => format(v)}
					/>
				)}
			</div>
		);
	}

	if (spec.chart.type === "table") {
		const columns = visibleColumns(data.columns, display);
		const numericColumns = comparison ? columns.filter((c) => isNumericColumn(data.rows, c)) : [];
		const thresholdColumns = thresholds?.length
			? display?.thresholdColumns?.length
				? display.thresholdColumns
				: columns.filter((c) => isNumericColumn(data.rows, c))
			: [];
		const cell = (row: Record<string, unknown>, c: string, key: string) => {
			const value = row[c];
			const numeric = typeof value === "number";
			const color = thresholdColumns.includes(c) ? thresholdColor(value, thresholds) : undefined;
			const href = !numeric ? drillPath(row, c, data.columns) : undefined;
			const text = numeric
				? format(value, undefined)
				: grainOfLabel(value)
				? formatBucketLabel(value, true)
				: String(value ?? "");
			return (
				<td
					key={key}
					className={cn(
						"px-2 py-1",
						numeric && "text-right tabular-nums",
						color && THRESHOLD_CLASSES[color].text,
						color && "font-medium"
					)}
				>
					{href ? (
						<Link
							to={href}
							className="text-action underline-offset-2 hover:underline"
							onClick={(e) => e.stopPropagation()}
						>
							{text}
						</Link>
					) : (
						text
					)}
				</td>
			);
		};
		return (
			<div className="h-full max-h-full overflow-auto">
				<table className="w-full text-sm">
					<thead className="sticky top-0 bg-white">
						<tr>
							{columns.map((c) => (
								<React.Fragment key={c}>
									<th className="border-b px-2 py-1 text-left font-medium text-gray-600">{c}</th>
									{numericColumns.includes(c) && (
										<>
											<th className="border-b px-2 py-1 text-right font-normal text-gray-400">
												{cmpLabel}
											</th>
											<th className="border-b px-2 py-1 text-right font-normal text-gray-400">
												Δ %
											</th>
										</>
									)}
								</React.Fragment>
							))}
						</tr>
					</thead>
					<tbody>
						{data.rows.slice(0, maxTableRows).map((row, i) => {
							const other = comparison?.rows[i];
							return (
								<tr
									key={i}
									className={cn("odd:bg-gray-50", clickable && "cursor-pointer hover:bg-brand/5")}
									onClick={
										clickable ? () => onPointClick?.({ xKey, value: row[xKey], row }) : undefined
									}
								>
									{columns.map((c) => {
										const numeric = numericColumns.includes(c);
										const pct = numeric ? deltaPercent(row[c], other?.[c]) : null;
										return (
											<React.Fragment key={c}>
												{cell(row, c, c)}
												{numeric && (
													<td className="px-2 py-1 text-right tabular-nums text-gray-400">
														{format(other?.[c], undefined)}
													</td>
												)}
												{numeric && (
													<td
														className={cn(
															"px-2 py-1 text-right tabular-nums text-xs",
															pct === null
																? "text-gray-400"
																: pct >= 0
																? "text-brand"
																: "text-danger"
														)}
													>
														{pct === null ? "–" : formatPercent(pct)}
													</td>
												)}
											</React.Fragment>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		);
	}

	const seriesColor = (k: string, i: number) =>
		display?.axis?.colors?.[k] ?? PALETTE[i % PALETTE.length];
	const tooltipFormatter = (value: unknown) => format(value);
	const pointFor = (payload: unknown): ChartPoint | undefined => {
		const row =
			(payload as { payload?: Record<string, unknown> })?.payload ??
			(payload as Record<string, unknown>);
		return row && typeof row === "object" ? { xKey, value: row[xKey], row } : undefined;
	};
	const handleClick = (payload: unknown) => {
		const point = pointFor(payload);
		if (point) onPointClick?.(point);
	};

	if (spec.chart.type === "pie") {
		const key = yKeys[0];
		return (
			<ResponsiveContainer width="100%" height={height}>
				<PieChart>
					<Pie
						data={data.rows}
						dataKey={key}
						nameKey={xKey}
						outerRadius="80%"
						label={(d) => format(d.value)}
						labelLine={false}
						onClick={clickable ? (d) => handleClick(d) : undefined}
						className={clickable ? "cursor-pointer" : undefined}
					>
						{data.rows.map((row, i) => (
							<Cell
								key={i}
								fill={display?.axis?.colors?.[String(row[xKey])] ?? PALETTE[i % PALETTE.length]}
							/>
						))}
					</Pie>
					<Tooltip formatter={tooltipFormatter} />
					{!display?.axis?.hideLegend && <Legend wrapperStyle={{ fontSize: 11 }} />}
				</PieChart>
			</ResponsiveContainer>
		);
	}

	// Time series: empty buckets become zero rows so lines do not skip weeks.
	const filled = data.grain ? fillGaps(data.rows, xKey, yKeys, data.grain, data.range) : data.rows;
	const rows = comparison ? mergeComparison({ ...data, rows: filled }, xKey, yKeys) : filled;
	const isBucketAxis = rows.length > 0 && rows.every((r) => grainOfLabel(r[xKey]));
	const xTick = (value: unknown) => (isBucketAxis ? formatBucketLabel(value) : String(value ?? ""));
	const labelFormatter = (
		label: unknown,
		payload: ReadonlyArray<{ payload?: Record<string, unknown> }>
	) => {
		const other = payload?.[0]?.payload?.__cmpLabel;
		const main = isBucketAxis ? formatBucketLabel(label, true) : String(label);
		return other !== undefined && other !== label
			? `${main} (${cmpLabel}: ${isBucketAxis ? formatBucketLabel(other, true) : String(other)})`
			: main;
	};
	const axisOptions = display?.axis;
	const rotate = axisOptions?.rotateLabels;
	const yDomain: [number | "auto" | "dataMin", number | "auto" | "dataMax"] = axisOptions?.log
		? ["auto", "auto"]
		: axisOptions?.yFromZero === false
		? ["auto", "auto"]
		: [0, "auto"];
	const average =
		axisOptions?.average && yKeys[0]
			? filled.reduce((sum, r) => sum + Number(r[yKeys[0]] ?? 0), 0) / Math.max(1, filled.length)
			: undefined;
	const references = (yAxisId?: string) => (
		<>
			{(display?.referenceLines ?? []).map((line, i) => (
				<ReferenceLine
					key={`ref-${i}`}
					y={line.value}
					yAxisId={yAxisId}
					stroke={line.color ?? chartColors.danger}
					strokeDasharray="6 3"
					label={
						line.label
							? {
									value: line.label,
									position: "insideTopRight",
									fontSize: 10,
									fill: line.color ?? chartColors.danger,
							  }
							: undefined
					}
				/>
			))}
			{average !== undefined && (
				<ReferenceLine
					y={average}
					yAxisId={yAxisId}
					stroke="#9ca3af"
					strokeDasharray="3 3"
					label={{
						value: `snitt ${format(average)}`,
						position: "insideBottomRight",
						fontSize: 10,
						fill: "#6b7280",
					}}
				/>
			)}
		</>
	);
	const xAxis = (
		<XAxis
			dataKey={xKey}
			tick={{ fontSize: 10 }}
			interval={rotate ? 0 : "preserveStartEnd"}
			tickFormatter={xTick}
			angle={rotate ? -40 : 0}
			textAnchor={rotate ? "end" : "middle"}
			height={rotate ? 56 : 30}
		/>
	);
	const axes = (
		<>
			<CartesianGrid stroke="#f0f0f0" vertical={false} />
			{xAxis}
			<YAxis
				tick={{ fontSize: 10 }}
				width={56}
				tickFormatter={axisFormat}
				domain={yDomain}
				scale={axisOptions?.log ? "log" : "auto"}
				allowDataOverflow={!!axisOptions?.log}
			/>
			<Tooltip formatter={tooltipFormatter} labelFormatter={labelFormatter} />
			{!axisOptions?.hideLegend && (yKeys.length > 1 || comparison) && (
				<Legend wrapperStyle={{ fontSize: 11 }} />
			)}
			{references()}
		</>
	);
	const stackId = spec.chart.stacked ? "s" : undefined;
	const cmpName = (k: string) => `${k} ${cmpLabel}`;
	const clickProps = clickable ? { onClick: handleClick, className: "cursor-pointer" } : {};

	if (spec.chart.type === "bar") {
		return (
			<ResponsiveContainer width="100%" height={height}>
				<BarChart data={rows}>
					{axes}
					{yKeys.map((k, i) => (
						<React.Fragment key={k}>
							<Bar
								dataKey={k}
								name={k}
								fill={seriesColor(k, i)}
								stackId={stackId}
								radius={[3, 3, 0, 0]}
								{...clickProps}
							/>
							{comparison && (
								<Bar
									dataKey={cmpKey(k)}
									name={cmpName(k)}
									fill={seriesColor(k, i)}
									fillOpacity={0.35}
									stackId={stackId ? `${stackId}-cmp` : undefined}
									radius={[3, 3, 0, 0]}
								/>
							)}
						</React.Fragment>
					))}
				</BarChart>
			</ResponsiveContainer>
		);
	}

	if (spec.chart.type === "combo") {
		const wantedLines = (spec.chart.lineKeys ?? []).filter((k) => yKeys.includes(k));
		const lineKeys = wantedLines.length
			? wantedLines
			: yKeys.length > 1
			? [yKeys[yKeys.length - 1]]
			: [];
		const barKeys = yKeys.filter((k) => !lineKeys.includes(k));
		const lineUnit = spec.chart.lineUnit;
		const rightAxis = !!lineUnit && lineUnit !== unit;
		const isLine = (name: unknown) => lineKeys.some((k) => name === k || name === cmpName(k));
		const comboFormatter = (value: unknown, name: unknown) =>
			format(value, isLine(name) ? lineUnit ?? unit : unit);
		return (
			<ResponsiveContainer width="100%" height={height}>
				<ComposedChart data={rows}>
					<CartesianGrid stroke="#f0f0f0" vertical={false} />
					{xAxis}
					<YAxis
						yAxisId="left"
						tick={{ fontSize: 10 }}
						width={56}
						tickFormatter={axisFormat}
						domain={yDomain}
					/>
					{rightAxis && (
						<YAxis
							yAxisId="right"
							orientation="right"
							tick={{ fontSize: 10 }}
							width={48}
							tickFormatter={axisFormat}
						/>
					)}
					<Tooltip formatter={comboFormatter} labelFormatter={labelFormatter} />
					{!axisOptions?.hideLegend && <Legend wrapperStyle={{ fontSize: 11 }} />}
					{references("left")}
					{barKeys.map((k) => {
						const i = yKeys.indexOf(k);
						return (
							<React.Fragment key={k}>
								<Bar
									yAxisId="left"
									dataKey={k}
									name={k}
									fill={seriesColor(k, i)}
									stackId={stackId}
									radius={[3, 3, 0, 0]}
									{...clickProps}
								/>
								{comparison && (
									<Bar
										yAxisId="left"
										dataKey={cmpKey(k)}
										name={cmpName(k)}
										fill={seriesColor(k, i)}
										fillOpacity={0.35}
										stackId={stackId ? `${stackId}-cmp` : undefined}
										radius={[3, 3, 0, 0]}
									/>
								)}
							</React.Fragment>
						);
					})}
					{lineKeys.map((k) => {
						const i = yKeys.indexOf(k);
						const axis = rightAxis ? "right" : "left";
						return (
							<React.Fragment key={k}>
								<Line
									yAxisId={axis}
									type="monotone"
									dataKey={k}
									name={k}
									stroke={seriesColor(k, i)}
									strokeWidth={2}
									dot={{ r: 3 }}
									activeDot={
										clickable
											? { onClick: (_e: unknown, p: unknown) => handleClick(p), cursor: "pointer" }
											: undefined
									}
								/>
								{comparison && (
									<Line
										yAxisId={axis}
										type="monotone"
										dataKey={cmpKey(k)}
										name={cmpName(k)}
										stroke={seriesColor(k, i)}
										strokeWidth={1.5}
										strokeDasharray="4 3"
										strokeOpacity={0.6}
										dot={false}
									/>
								)}
							</React.Fragment>
						);
					})}
				</ComposedChart>
			</ResponsiveContainer>
		);
	}

	const activeDot = clickable
		? { onClick: (_e: unknown, p: unknown) => handleClick(p), cursor: "pointer", r: 5 }
		: undefined;

	if (spec.chart.type === "area") {
		return (
			<ResponsiveContainer width="100%" height={height}>
				<AreaChart data={rows}>
					{axes}
					{yKeys.map((k, i) => (
						<React.Fragment key={k}>
							<Area
								type="monotone"
								dataKey={k}
								name={k}
								stroke={seriesColor(k, i)}
								fill={seriesColor(k, i)}
								fillOpacity={0.2}
								stackId={stackId}
								activeDot={activeDot}
							/>
							{comparison && (
								<Area
									type="monotone"
									dataKey={cmpKey(k)}
									name={cmpName(k)}
									stroke={seriesColor(k, i)}
									strokeDasharray="4 3"
									fill="none"
									strokeOpacity={0.6}
								/>
							)}
						</React.Fragment>
					))}
				</AreaChart>
			</ResponsiveContainer>
		);
	}

	return (
		<ResponsiveContainer width="100%" height={height}>
			<LineChart data={rows}>
				{axes}
				{yKeys.map((k, i) => (
					<React.Fragment key={k}>
						<Line
							type="monotone"
							dataKey={k}
							name={k}
							stroke={seriesColor(k, i)}
							strokeWidth={2}
							dot={false}
							activeDot={activeDot}
						/>
						{comparison && (
							<Line
								type="monotone"
								dataKey={cmpKey(k)}
								name={cmpName(k)}
								stroke={seriesColor(k, i)}
								strokeWidth={1.5}
								strokeDasharray="4 3"
								strokeOpacity={0.6}
								dot={false}
							/>
						)}
					</React.Fragment>
				))}
			</LineChart>
		</ResponsiveContainer>
	);
};

export default WidgetChart;
